<template>
  <div
    class="flex flex-col slide-generator"
    :class="{
      'expand': isGeneratorContentVisible,
      'is-modal-content': isModalContent,
      '--has-expanded-examples': isExpandExampleList,
      '--has-search-content': isSearchAIModalContentVisible,
    }"
  >
    <div
      class="quiz-header"
      :class="{
        'expand': isGeneratorContentVisible
      }"
    >
      <div class="quiz-header-inner">
        <div
          class="quiz-header-inner__title"
          :class="{'items-center mb-5': isModalContent}"
        >
          <div
            class="back-search"
            :class="{'text-center justify-center': isModalContent && !isSearchAIModalContentVisible}"
          >
            <span v-show="isArrowBackVisible" class="pr-3 cursor-pointer" @click.stop="backToChoiceType">
              <i class="fas fa-chevron-left"></i>
            </span>
            <img
              v-show="!isGeneratorContentVisible || isModalContent"
              class="inline-block mr-1 mb-1"
              src="@/assets/images/icon/sparkles_2728.png"
              alt=""
              width="20"
              height="20"
            >
            <span
              v-if="isGeneratorContentVisible"
              class="quiz-header-inner__title-text"
            >{{ $i18nPresenter.t('AI Slide Generator') }}</span>
            <span v-else class="quiz-header-inner__title-no-prompt">{{ $i18nPresenter.t('AI Slide Generator') }}</span>
          </div>
          <div
            v-if="!isModalContent"
            class="quiz-header-inner__arrow-wrapper"
            @click="handleCollapseAISlideGenerator()"
          >
            <aha-icon
              name="arrow-no-tail-white"
              width="18"
              height="18"
              viewBox="0 0 16 16"
              class="quiz-header-inner__arrow"
              :class="{'arrow-up': isAIGeneratorCollapsed}"
            />
          </div>
          <aha-icon
            name="close"
            height="20"
            width="20"
            viewBox="0 0 24 24"
            class="cursor-pointer text-base-50 hover:text-base-0"
            @click="handleCloseModal()"
            v-if="isModalContent"
          />
        </div>

        <aha-textarea
          class="slide-generator-input"
          :class="{'hidden': isAITextAreaHidden}"
          id="slide-generator-input"
          :placeholder="quizPlaceholder"
          v-model="prompt"
          :maxLength="500"
          @focus="focusAIInput"
          @keyupEnter="handleGenerateSlides('Enter')"
          hasInnerButton
          preventEnterNewLine
          @buttonClick="handleGenerateSlides('Button clicked')"
        >
          <template #button>
            <slot name="button">
              <aha-icon
                name="ai-generate-button-disabled"
                :width="isModalContentNoSearchResult ? 29 : 25"
                :height="isModalContentNoSearchResult ? 28 : 24"
                viewBox="0 0 29 28"
                maintainColor
                class="cursor-not-allowed"
                v-show="isGenerateButtonDisabled"
              />
              <aha-icon
                name="ai-generate-button"
                :width="isModalContentNoSearchResult ? 28 : 25"
                :height="isModalContentNoSearchResult ? 28 : 25"
                viewBox="0 0 24 24"
                maintainColor
                v-show="!isGenerateButtonDisabled"
                v-tooltip="(isSubmitting || isContentListLoadingVisible) ? '' : buttonTooltipContent"
              />
            </slot>
          </template>
        </aha-textarea>
        <div class="mt-4 example-area" :class="{'hidden': isExampleAreaHidden}">
          <template v-for="(example, index) in exampleList">
            <div
              class="example-box"
              @click="showQuizExample(example)"
              :key="'A' + index"
              v-if="index <= 1"
            >
              <aha-skeleton-loader
                :width="isModalContent ? '160px' : '147.5px'"
                :height="isModalContent ? '20px' : '17px'"
                v-if="!example.content"
              />
              <div v-else>
                <img
                  class="inline-block mr-1"
                  src="@/assets/images/icon/sparkles_2728.png"
                  width="16"
                >
                <span class="example-box__text">{{ example.content }}</span>
              </div>
            </div>
            <div
              class="example-box"
              @click="showQuizExample(example)"
              :key="'B' + index"
              v-if="index > 1 && isExpandExampleList"
            >
              <aha-skeleton-loader
                :width="isModalContent ? '160px' : '147.5px'"
                :height="isModalContent ? '20px' : '17px'"
                v-if="!example.content"
              />
              <div v-else>
                <img
                  class="inline-block mr-1"
                  src="@/assets/images/icon/sparkles_2728.png"
                  width="16"
                >
                <span class="example-box__text">{{ example.content }}</span>
              </div>
            </div>
            <div
              class="inline-block ml-1 mb-2 cursor-pointer rounded-md expand-arrow-box"
              @click.stop="expandExampleList()"
              :key="index"
              v-if="index === 1 && !isExpandExampleList"
            >
              <aha-icon
                name="arrow-no-tail-white"
                width="13"
                height="13"
                viewBox="0 0 16 16"
                class="expand-arrow-box__icon"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
    <div
      v-if="isGeneratedResultsVisible"
      class="search-result"
    >
      <div
        v-if="isSubmitting"
        class="flex content-center justify-center w-full h-full text-center"
      >
        <div class="mt-9">
          <div class="text-base-100">
            {{ $i18nPresenter.t("Our robot's on it 🤖 Don't go anywhere!") }}
          </div>
          <FakeProgressbar :resetProgressBar="resetProgressBar" />
        </div>
      </div>
      <div v-else class="search-result-inner">
        <div v-if="chatResponseError" class="text-center mt-15">
          <AIStumped />
        </div>
        <div class="content-list" v-else-if="!isSubmitting && chatResponse">
          <div v-for="(response, index) in chatResponse" :key="response.id">
            <AIGeneratedPickAnswerContent
              v-if="response.type === AIResponseType.PICK_ANSWER"
              :response="response"
              :isAddSlideButtonsDisabled="isAddSlideButtonsDisabled"
              :index="index"
              @handleAddSlideToPresentation="handleAddSlideToPresentation"
              :isModalContent="isModalContent"
              class="content-item"
            />
            <AIGeneratedFreestyleContent
              v-if="response.type === AIResponseType.FREESTYLE"
              :response="response"
              :isAddSlideButtonsDisabled="isAddSlideButtonsDisabled"
              :index="index"
              @handleAddSlideToPresentation="handleAddSlideToPresentation"
              :isModalContent="isModalContent"
              class="content-item"
            />
            <AIGeneratedTypeAnswerContent
              v-if="response.type === AIResponseType.TYPE_ANSWER"
              :response="response"
              :isAddSlideButtonsDisabled="isAddSlideButtonsDisabled"
              :index="index"
              @handleAddSlideToPresentation="handleAddSlideToPresentation"
              :isModalContent="isModalContent"
              class="content-item"
            />
          </div>
          <div class="content-list__loading" v-if="isContentListLoadingVisible">
            <div v-if="isReturnedSlideError" class="text-center mt-15 h-0">
              <AIStumped />
            </div>
            <aha-spinner v-else />
          </div>
        </div>
      </div>
    </div>

    <aha-modal
      v-if="isShowModalLimit"
      id="modal-request-limit"
      :title="modalLimitInfo.title"
      @hide="handleModalConfirm(false)"
    >
      <div class="text-base-90">
        {{ modalLimitInfo.body }}
      </div>

      <template #action>
        <aha-link
          v-if="modalLimitInfo.isReachedMaximum"
          @click="handleModalConfirm(false)"
        >
          <span class="font-bold">{{ $i18nPresenter.t('No, thanks') }}</span>
        </aha-link>
        <aha-button
          v-if="modalLimitInfo.isReachedMaximum"
          variant="primary"
          has-margin-left
          @click="handleModalConfirm(true)"
        >
          {{ $i18nPresenter.t('Get updates') }}
        </aha-button>
        <aha-button
          v-if="modalLimitInfo.isReachedThreshold"
          variant="primary"
          has-margin-left
          @click="handleModalConfirm(false)"
        >
          {{ $i18nPresenter.t('Okay') }}
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import mixpanelUtils from '@/utils/mixpanel.utils'
import textUtils from '@/utils/textUtil'
import eventCategories from '@/constant/eventCategories'
import AIResponseType from '@/constant/aiResponseType'

import { generateBlockHash, generateBlockId } from '@/utils/canvas-block-layouts/common'
import utils from '@/util'
import OpenaiApi from '@/api/openai'
import SlideApi from '@/api/slide'
import UserApi from '@/api/user'
import AIResponseAPI from '@/api/aiResponse'
import EventBus from '@/eventBus'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import PresentationUtils from '@/utils/presentation'

import FakeProgressbar from '../FakeProgressbar.vue'
import AIGeneratedPickAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedPickAnswerContent.vue'
import AIGeneratedFreestyleContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedFreestyleContent.vue'
import AIGeneratedTypeAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedTypeAnswerContent.vue'
import AIStumped from '@/components-v2/presentation-editor/new-slide/OpenAI/AIStumped.vue'
import CanvasBlocksUpdateMixin from '@/mixins/presentation/CanvasBlocksUpdateMixin'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'

const REQUEST_MAXIMUM = 150
const REQUEST_THRESHOLD = 140
const DAYS_30 = 30 * 24 * 60 * 60 * 1000
const AI_SLIDES_THRESHOLD = 10

export default {
  components: {
    FakeProgressbar,
    AIGeneratedPickAnswerContent,
    AIGeneratedFreestyleContent,
    AIGeneratedTypeAnswerContent,
    AIStumped,
  },
  mixins: [CanvasBlocksUpdateMixin, UndoRedoMixin],
  props: {
    isGeneratorContentVisible: {
      type: Boolean,
      default: false,
    },
    isModalContent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      prompt: '',
      chatResponse: '',
      chatResponseError: false,
      isSubmitting: false,
      isShowModalLimit: false,
      quizPlaceholder: 'Enter a topic or paste information',
      exampleList: [0, 1, 2, 3, 4], // fake for skeleton loader
      isExpandExampleList: false,
      gaGenerateFailedAction: 'AI Quiz Generator > Generate > Failed',
      gaGenerateSuccessfulAction: 'AI Quiz Generator > Generate > Successful',
      gaSideLeftModalText: 'Create new slide modal',
      AIResponseType,
      isSearchAIModalContentVisible: false,
      buttonTooltipContent: null,
      tooltipText: 'Generate',
      hasReachedTooltipAutoShownLimit: false,
      resetProgressBar: 0,
      isReturnedSlideError: false,
      isExampleResponse: false,
      isAddSlideButtonsDisabled: false,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isNewPresentation: (state) => state.isNewPresentation,
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
      currentUser: (state) => state.currentUser,
      slideGenerator: (state) => state.slideGenerator,
    }),
    ...mapGetters(['isEURegion']),
    isGeneratedResultsVisible() {
      return this.isGeneratorContentVisible && this.isSearchAIModalContentVisible
    },
    hasInputPrompt() {
      return this.prompt && this.prompt.length > 0
    },
    modalLimitInfo() {
      // slideGeneratorRequestCount >= REQUEST_MAXIMUM
      let title = "You've reached the beta access limit."
      let body = 'Thanks for using the beta version of the AI Quiz Generator! Get updates about the full release of this feature by clicking the button below.'
      const requestCount = (this.currentUser && this.currentUser.slideGeneratorRequestCount) || -1

      if (requestCount === REQUEST_THRESHOLD) {
        title = 'You have 10 uses left.'
        body = 'This beta version is limited to 150 uses. Use them wisely!'
      }

      return {
        isReachedThreshold: requestCount === REQUEST_THRESHOLD,
        isReachedMaximum: requestCount >= REQUEST_MAXIMUM,
        title,
        body,
      }
    },
    isArrowBackVisible() {
      return this.isGeneratorContentVisible && !this.isModalContent
    },
    isPickAnswerType() {
      return this.slideActive.id && this.slideActive.type === 'pickAnswer'
    },
    isPickAnswerSlide() {
      return this.isPickAnswerType && !this.slideActive.slideType
    },
    isTypeAnswerSlide() {
      return this.isPickAnswerType && this.slideActive.slideType === 'typeAnswer'
    },
    aiRequestSlideType() {
      if (!this.isModalContent) return null
      if (this.isPickAnswerSlide) return 'pickAnswer'
      if (this.isTypeAnswerSlide) return 'typeAnswer'
      if (this.slideActive.type === 'freestyle') return 'freestyle'
      return null
    },
    keyShowTooltip() {
      return `has-shown-ai-submit-button-tooltip-${this.currentUser.id}`
    },
    isHoverTooltipVisible() {
      return this.hasReachedTooltipAutoShownLimit || this.isSearchAIModalContentVisible ||
      !this.hasInputPrompt || this.isSubmitting
    },
    isAIGeneratorCollapsed() {
      return this.currentUser.rememberSettings.isAIGeneratorCollapsed
    },
    isExampleAreaHidden() {
      return (this.isAIGeneratorCollapsed && !this.isModalContent) || this.isSearchAIModalContentVisible
    },
    isAITextAreaHidden() {
      return this.isAIGeneratorCollapsed && !this.isModalContent
    },
    isModalContentNoSearchResult() {
      return this.isModalContent && !this.isSearchAIModalContentVisible
    },
    isContentListLoadingVisible() {
      return this.chatResponse && this.chatResponse.length &&
        this.chatResponse.length < AI_SLIDES_THRESHOLD && !this.chatResponse[0].id && !this.isExampleResponse
    },
    noChatResponse() {
      return !this.chatResponse || (this.chatResponse && !this.chatResponse.length)
    },
    isGenerateButtonDisabled() {
      return !this.hasInputPrompt || this.isSubmitting ||
        (this.isContentListLoadingVisible && !this.isReturnedSlideError)
    },
  },
  watch: {
    hasInputPrompt() {
      this._setButtonTooltipContent()
      this.setIsAIPromptHasInputAction(this.hasInputPrompt) // to blur select slide type in search popup
    },
  },
  async mounted() {
    this.setIsAIPromptHasInputAction(this.hasInputPrompt)
    this._setTooltipLocalStorageLimit()
    EventBus.$on('slide-search-popup:open', this.backToChoiceType)
    EventBus.$on('slide-generator:unlock-add-slide', () => {
      if (!this.chatResponse || !this.chatResponse.length) return
      this.chatResponse.forEach((res) => {
        res.isAddingSlide = false
      })
    })
    EventBus.$on('slide-generator:generated', (socketData) => {
      this._handleGenerateSlidesSuccess(socketData)
    })
    EventBus.$on('slide-generator: ai_response_created', (socketData) => {
      if (!socketData || !socketData.length) {
        this.chatResponseError = true
      }
      this.chatResponse = this.chatResponse.map((res, resIndex) => {
        return { ...res, ...socketData[resIndex] }
      })
      this.isAddSlideButtonsDisabled = false
    })
    if (this.isModalContent) {
      this.isExpandExampleList = true
    }
    await this.getExamples()
  },
  destroyed() {
    EventBus.$off('slide-generator:unlock-add-slide')
    EventBus.$off('slide-generator:generated')
    EventBus.$off('slide-generator: ai_response_created')
  },
  methods: {
    ...mapActions(['setSlideActiveAction', 'setCurrentUserAction', 'setIsAIPromptHasInputAction']),

    backToChoiceType() {
      if (this.isModalContent) return
      this.isSearchAIModalContentVisible = false
      this._changeAIModalSize('3xl')
      this.prompt = ''
      this.isExpandExampleList = false
      this.$emit('handleDiveInQuiz', false)
    },

    focusAIInput() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Focus input',
        this.prompt,
        null,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
    },

    async getExamples() {
      try {
        this.exampleList = await OpenaiApi.getExamples()
      } catch {
        Sentry.captureException({
          error: 'Get Examples',
        })
      }
    },

    handleGenerateSlides(mode) {
      if (this.isContentListLoadingVisible) return
      this.isAddSlideButtonsDisabled = true
      this.isReturnedSlideError = false
      this.isExampleResponse = false
      this.chatResponse = []
      if (this.isSubmitting) {
        this.resetProgressBar += 1
      }
      if (!this.prompt || !this.prompt.trim().length) return
      const { slideGeneratorRequestCount } = this.$store.state.currentUser

      if (slideGeneratorRequestCount >= REQUEST_MAXIMUM) {
        this.isShowModalLimit = true
        return
      }
      this.isSearchAIModalContentVisible = true
      this._changeAIModalSize('xl')
      this.isSubmitting = true
      this.buttonTooltipContent = this.tooltipText
      if (!this.isModalContent) {
        this.isExpandExampleList = false
      }
      this.$emit('handleDiveInQuiz', true)
      if (!this.isEURegion) {
        this._handleTrackGA(mode)
      }

      const payload = this._setPayloadGenerateSlide()
      try {
        OpenaiApi.generateSlide(payload)
      } catch (error) {
        this.isSubmitting = false
        this.chatResponseError = true
        this._trackGAWhenGenerateSlidesFailed()
        Sentry.captureException(error, {
          extra: {
            inputText: this.prompt,
          },
        })
      }
    },

    _handleTrackGA(mode) {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Generate',
        this.prompt,
        `Mode: ${mode}`,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
      mixpanelUtils.trackTimeEvent(this.gaGenerateFailedAction)
      mixpanelUtils.trackTimeEvent(this.gaGenerateSuccessfulAction)
    },

    _trackGAWhenGenerateSlidesFailed() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaGenerateFailedAction,
        this.prompt,
        null,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
    },

    _setPayloadGenerateSlide() {
      let payload = {
        message: this.prompt,
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      }
      if (this.aiRequestSlideType) {
        payload = {
          ...payload,
          aiRequestTypes: [this.aiRequestSlideType],
        }
      }
      return payload
    },

    _handleGenerateSlidesSuccess(res) {
      if (this.prompt !== res.request) return
      const hasResponse = res && res.responses && res.responses.data && res.responses.data.length
      const hasError = res && res.responses && res.responses.error
      if (res.slideGeneratorRequestCount) {
        this._updateUserRequestCount(res.slideGeneratorRequestCount)
      }

      if (res.slideGeneratorRequestCount === REQUEST_THRESHOLD
      || res.slideGeneratorRequestCount === REQUEST_MAXIMUM) {
        this.isShowModalLimit = true
      }

      this.isSubmitting = false
      if (hasResponse) {
        this.chatResponseError = false
        this.chatResponse = [...this.chatResponse, res.responses]
      }

      if (!hasResponse || hasError) {
        if (this.noChatResponse) {
          this.chatResponse = null
          this.chatResponseError = true
        } else {
          this.isReturnedSlideError = true
        }
      }

      if (this.chatResponseError) {
        this._trackGAWhenGenerateSlidesFailed()
      } else {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          this.gaGenerateSuccessfulAction,
          this.prompt,
          null,
          {
            presentationId: this.presentation.id,
          },
        )
      }
    },

    _updateUserRequestCount(slideGeneratorRequestCount) {
      this.setCurrentUserAction({
        ...this.currentUser,
        slideGeneratorRequestCount,
      })
    },

    _setLoadingButtonAdd(response, value) {
      this.chatResponse = this.chatResponse.map((res) => {
        return {
          ...res,
          isDisabled: value,
          isAddingSlide: response.id === res.id ? value : false,
        }
      })
    },

    async handleAddSlideToPresentation(response) {
      if (response.isAddingSlide) return
      this._setLoadingButtonAdd(response, true)
      if (this.isModalContent) {
        EventBus.$on('ai-generator-modal: add-new-slide')
      }
      try {
        const [payload, newSlideIndex, activeIndex] = this._setPayloadCreateGeneratedSlide(
          response.id,
        )
        const res = await AIResponseAPI.createSlide(response.id, payload)
        this._handleAfterAddSlide(res.body, newSlideIndex, activeIndex)
      } catch (error) {
        utils.showToastError(this.$i18nPresenter.t('Cannot add this slide to current presentation. Please try again'))
        Sentry.captureException(error)
      } finally {
        this._setLoadingButtonAdd(response, false)
      }
      const trackingSlideType = this._setTrackingSlideTypeText(response.type)
      utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Create new slide', 'AI-generated', trackingSlideType, {
        ...mixpanelUtils.getPresentationContext(this.$store.state.presentation),
        slideId: this.slideActive.id,
        slideType: trackingSlideType,
        touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal && this.isModalContent ?
          this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
      })
    },

    _setTrackingSlideTypeText(type) {
      let text = type
      if (type === this.AIResponseType.TYPE_ANSWER) {
        text = `${this.AIResponseType.PICK_ANSWER} ${this.AIResponseType.TYPE_ANSWER}`
      }
      return text
    },

    _setPayloadCreateGeneratedSlide(aiResponseId) {
      const payload = {
        aiResponseId,
        presentationId: this.presentation.id,
        textColour: this.slideActive.textColour || '#313131',
        baseColour: this.slideActive.baseColour || '#ffffff',
        backgroundImage: this.slideActive.backgroundImage,
        visibility: this.slideActive.visibility,
      }

      // set payload.order for new slide
      const [newSlideIndex, activeIndex] = this._setPayloadInsertAfter(payload)
      return [payload, newSlideIndex, activeIndex]
    },

    _setPayloadInsertAfter(payload) {
      const activeIndex = this.$store.state.presentation.slides.findIndex((s) => {
        if (s.id) {
          return s.id === this.$store.state.slideActive.id
        }
        return s.timestamp === this.$store.state.slideActive.timestamp
      })
      let newSlideIndex = activeIndex
      if (activeIndex > -1 && !this.isNewPresentation) {
        const isNextSlideLeaderboard = this.presentation.slides[activeIndex + 1] && this.presentation.slides[activeIndex + 1].type === 'leaderboard'
        const activeSlideId = this.presentation.slides[activeIndex] &&
          this.presentation.slides[activeIndex].id

        if (!activeSlideId) {
          payload.insertAfter = this.presentation.slides[activeIndex - 1].order
        }
        if (activeSlideId && !isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order
          newSlideIndex = activeIndex + 1
        }
        if (activeSlideId && isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order + 1
          newSlideIndex = activeIndex + 2
        }
      }
      return [newSlideIndex, activeIndex]
    },

    _handleAfterAddSlide(newSlide, newSlideIndex, activeIndex) {
      const isFreestyleSlideWithCanvasBlocks = newSlide.type === this.AIResponseType.FREESTYLE &&
        newSlide.canvasBlocks
      const isTypeAnswerSlide = newSlide.type === this.AIResponseType.PICK_ANSWER &&
        newSlide.slideType === this.AIResponseType.TYPE_ANSWER

      if (isFreestyleSlideWithCanvasBlocks) {
        const newCanvasBlocks = {}
        newSlide.canvasBlocks.forEach((block) => {
          block.id = generateBlockId(newSlide.id)
          block.h = generateBlockHash()
          newCanvasBlocks[block.id] = {
            id: block.id,
            type: 'RichText',
            tag: block.tag,
            content: block.content,
            style: block.style,
            h: block.h,
          }
        })
        newSlide.canvasBlocks = newCanvasBlocks
        newSlide.activeTab = 3
        this.updateCanvasBlocks(newSlide.canvasBlocks, newSlide.id) // CanvasBlocksUpdateMixin
        this.backupBlocks(newSlide.canvasBlocks, newSlide.canvasBlocks)
      } else if (isTypeAnswerSlide) {
        newSlide.otherCorrectQuiz = JSON.parse(newSlide.otherCorrectQuiz)
      }
      const defaultSlideId = this.presentation.slides[activeIndex].id
      this.$store.state.presentation.slides[newSlideIndex] = newSlide
      this.setSlideActiveAction(newSlide)
      EventBus.$emit(`update-right-column-for-slide-title-${this.slideActive.id}`, newSlide.title)
      if (PresentationUtils.needSuggestPresentationTitle()) {
        this._handleSuggestPresentationTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
      }
      setTimeout(() => {
        if (!this.slideGenerator.isAISlideGeneratorModalVisible) {
          EventBus.$emit('makeSlideActive', this.presentation.slides[newSlideIndex])
        }
        EventBus.$emit('SideLeft: setSelectedSlideIds', [newSlide.id])
        utils.showToastSuccess(this.$i18nPresenter.t('Slide added successfully'))
      }, 250)
      if (this.isNewPresentation) {
        this.$store.state.isNewPresentation = false
        // Delete default slide: poll
        if (defaultSlideId) {
          SlideApi.updateSlideById(defaultSlideId, {
            deleted: true,
          })
        }
      }
      if (newSlide.type === this.AIResponseType.PICK_ANSWER) {
        EventBus.$emit('addSlideLeaderboard')
      }
    },

    backupBlocks(oldData, newData, previousSelectingBlocks) {
      this.addActionToHistory({
        type: 'UpdateBlocks',
        data: {
          canvasBlocks: oldData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        newData: {
          canvasBlocks: newData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        slideActiveId: this.slideActive.id,
      })
    },

    _transformData(data) {
      return data.map((item) => {
        // Extracting the necessary fields
        const { id, index } = item
        const { type } = item.content
        const data = item.content.data.map((dataItem) => {
          if (type === 'pickAnswer' || type === 'typeAnswer') {
            return {
              questionTitle: dataItem.questionTitle,
              slideOptions: dataItem.slideOptions,
              correctQuizTypeAnswer: dataItem.correctQuizTypeAnswer,
              otherCorrectQuiz: dataItem.otherCorrectQuiz,
            }
          }

          return {
            type: dataItem.type,
            tag: dataItem.tag,
            content: dataItem.content,
            style: dataItem.style,
          }
        })

        return {
          id, index, type, data,
        }
      })
    },

    showQuizExample(example) {
      this.isSearchAIModalContentVisible = true
      this.isExampleResponse = true
      this._changeAIModalSize('xl')
      this.isSubmitting = true
      this.buttonTooltipContent = this.tooltipText
      this.prompt = example.content
      this.$emit('handleDiveInQuiz', true)
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Example',
        null,
        null,
        {
          presentationId: this.presentation.id,
          exampleContent: example.content,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
      setTimeout(() => {
        const transformedResponses = this._transformData(example.responses)
        this.chatResponse = transformedResponses
        this.isSubmitting = false
      }, 600)
    },

    _changeAIModalSize(size) {
      if (!this.isModalContent) return
      this.$emit('changeAIModalSize', size)
    },

    expandExampleList() {
      if (this.isModalContent) return
      this.isExpandExampleList = true
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > More Example',
        null,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },

    async handleModalConfirm(confirm = false) {
      if (confirm) {
        await OpenaiApi.requestMoreQuiz({
          plan: this.$store.state.plan,
        })
        utils.showToastSuccess(this.$i18nPresenter.t("Thanks! We'll update you via email."))
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'AI Quiz Generator > Over limit > Send request',
          null,
          null,
          {
            presentationId: this.presentation.id,
          },
        )
      }

      this.isShowModalLimit = false
    },

    handleCloseModal() {
      this.$emit('closeModal')
    },

    _setTooltipLocalStorageLimit() {
      this.buttonTooltipContent = null
      const countHasAutoShownTooltip = localStorageExpiry.getWithExpiry(this.keyShowTooltip) || 0
      this.hasReachedTooltipAutoShownLimit = countHasAutoShownTooltip && countHasAutoShownTooltip >= 3
      if (!this.hasReachedTooltipAutoShownLimit) {
        localStorageExpiry.setWithExpiry(this.keyShowTooltip, countHasAutoShownTooltip + 1, DAYS_30)
      }
    },

    _setButtonTooltipContent() {
      if (!this.hasInputPrompt) {
        this.buttonTooltipContent = null
      }
      setTimeout(() => {
        if (this.isHoverTooltipVisible) {
          this.buttonTooltipContent = this.tooltipText
          return
        }
        if (this.hasInputPrompt) {
          this.buttonTooltipContent = {
            content: this.tooltipText,
            show: this.hasInputPrompt,
          }
        }
      }, 300)
    },

    async handleCollapseAISlideGenerator() {
      try {
        const payload = {
          isAIGeneratorCollapsed: !this.currentUser.rememberSettings.isAIGeneratorCollapsed,
        }
        if (this.hasInputPrompt) {
          this.setIsAIPromptHasInputAction(!payload.isAIGeneratorCollapsed)
        }
        this.setCurrentUserAction({
          ...this.currentUser,
          rememberSettings: {
            ...this.currentUser.rememberSettings,
            ...payload,
          },
        })
        await UserApi.updateSettings(payload)
      } catch (error) {
        //
      }
    },

    _getSlideTitle(newSlide, isFreestyleSlideWithCanvasBlocks) {
      if (isFreestyleSlideWithCanvasBlocks) {
        return textUtils.getFirstCanvasBlockContent(newSlide.canvasBlocks)
      }
      return newSlide.title
    },

    _handleSuggestPresentationTitle(newSlide, isFreestyleSlideWithCanvasBlocks) {
      const newSlideTitle = this._getSlideTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
      PresentationUtils.handleSuggestPresentationTitle(newSlideTitle)
    },
  },
}
</script>

<style lang="scss" scoped>
.slide-generator {
  background-color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  &.expand {
    height: 100%;
    background-color: transparent;
  }
  &.is-modal-content {
    .slide-generator-input {
      &::v-deep {
        .aha-textarea {
          height: 4rem;
          min-height: 4rem;
          max-height: 25rem;
          font-size: 20px;
          padding: 20px 16px;
          padding-right: 5.5rem;
          overflow: auto;
          width: 624px;
          margin: 0 auto;
        }
        .aha-textarea-limit {
          font-size: 20px;
          bottom: 1.25rem;
          right: 8rem;
        }
        .aha-textarea-button {
          bottom: 1.1rem;
          right: 6rem;
        }
      }
    }
    .example-area {
      text-align: center;
      .example-box {
        margin-bottom: 8px;
        padding: 8px 12px;
      }
      .example-box__text {
        display: inline-block;
        font-size: 14px;
      }
    }
    .search-result {
      overflow: auto;
    }
    &.--has-search-content {
      .quiz-header-inner {
        padding: 10px 20px 20px 20px;
        .quiz-header-inner__title {
          padding-top: 10px;
          margin-bottom: 16px;
        }
      }
      .slide-generator-input {
        &::v-deep {
          .aha-textarea {
            width: 100%;
            height: 2.75rem;
            min-height: 2.75rem;
            padding: 0.6rem 4.3rem 0.6rem 0.75rem;
            font-size: 14px;
          }
          .aha-textarea-limit {
            right: 2.75rem;
            bottom: 0.75rem;
            font-size: 14px;
          }
          .aha-textarea-button {
            right: 1rem;
            bottom: 0.7rem;
          }
        }
      }
        .search-result {
        height: calc(90vh - 150px);
        .search-result-inner {
          padding-top: 1rem;
          .content-list {
            padding-bottom: 0;
            width: 468px;
            margin: 0 auto;
          }
        }
      }
    }
  }
  &.--has-expanded-examples {
    &::v-deep+.new-slide-content__layer {
      height: calc(100% - 272px);
    }
  }
  &.--has-search-content {
    .quiz-header-inner {
      border-radius: 0;
      padding: 10px 20px;
      .back-search {
        font-size: 0.9rem;
        .quiz-header-inner__title-text {
          font-size: 16px;
        }
      }
    }
  }
}

.quiz-header {
  padding: 16px 20px 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.quiz-header.expand {
  padding: 0;

  .quiz-header-inner {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.quiz-header-inner {
  position: relative;
  background-color: #7564D2;
  border-radius: 8px;
  padding: 0.9rem;
  transition: cubic-bezier(0.075, 0.82, 0.165, 1);
  overflow: hidden;
  .quiz-header-inner__title {
    @apply flex items-center;

    .back-search {
      flex: 1;
      display: flex;
      align-items: center;
      .quiz-header-inner__title-no-prompt {
        font-size: 16px;
      }
    }
  }
  .quiz-header-inner__arrow-wrapper {
    padding: 0.5rem 0.5rem 0.75rem 0.5rem;
    @apply cursor-pointer text-base-0 hover:text-base-30;
    .quiz-header-inner__arrow {
      transition: 0.2s ease;
      transform: rotate(180deg);
      &.arrow-up {
        transform: rotate(0);
      }
    }
  }

  &::after {
    z-index: 0;
    position: absolute;
    content: '';
    border-radius: 50%;
    background: linear-gradient(160.79deg, #1D1D54 9.94%, rgba(29, 29, 84, 0) 27.1%);
    opacity: 0.62;
    width: 1176.68px;
    height: 1279px;
    left: -40px;
    top: 52px;
  }
  .btn-generate-area {
    display: block;
    text-align: center;
  }
  .example-area {
    color: white;
    min-height: 40px;
    .example-box {
      @apply inline-block ml-1 mb-2 cursor-pointer rounded-md;
      background-color: rgba(0, 0, 0, 0.4);
      padding: 6px 8px;
      border-radius: 6px;
      position: relative;
      z-index: 1;
      font-size: 12px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.5);
      }
      &::v-deep {
        .skeleton-loader {
          border-radius: 6px;
          background-color: rgba(0, 0, 0, 0.4);
        }
      }
    }
    .expand-arrow-box {
      background-color: rgba(0, 0, 0, 0.4);
      padding: 6px 12px;
      border-radius: 6px;
      position: relative;
      z-index: 1;
      font-size: 12px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.4);
      }

      .expand-arrow-box__icon {
        transform: rotate(180deg);
      }
    }
  }

}

.back-search {
  @apply text-xl font-bold text-base-0;
  padding-right: 16px;
}

.search-result {
  height: 100%;
  padding-bottom: 1rem;
  display: flex;
  background-color: #F9FBFF;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
}

.search-result-inner {
  padding: 0 1rem;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -ms-overflow-style: auto;
}

.content-list {
  .content-item {
    @apply shadow rounded-lg my-4;
    @apply w-full;
    color: #000;
    background-color: #fff;
    &::v-deep {
      ul {
        list-style: disc;
        padding-left: 1rem;
      }
      p:not(:last-child) {
        padding-bottom: 0.75rem;
      }
    }
  }
  .content-list__loading {
    @apply w-full h-8 flex items-center justify-center;
  }
}
.slide-generator-input {
  z-index: 1;
  position: relative;
  width: 100%;
  &::v-deep {
    .aha-textarea {
      padding-right: 4.3rem;
      height: 2.75rem;
      min-height: 2.75rem;
      max-height: 8.75rem;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      padding-left: 0.75rem;
    }
    .aha-textarea-limit {
      right: 2.75rem;
      bottom: 0.75rem;
    }
    .aha-textarea-button {
      @apply absolute right-4;
      bottom: 0.7rem;
    }
  }
}
</style>
